import React, { createContext, useContext, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { useAppContext } from '~/app-context'
import type { SidepanelLogicState } from './logic'
import { SidepanelLogic } from './logic'
import { Button } from '~common/components/button'

const SidepanelContext = createContext<SidepanelLogic | null>(null)

export function useSidepanel(): {
    isOpen: boolean
    open: (content: React.ReactNode, options?: { width?: number }) => void
    close: () => void
} {
    const logic = useContext(SidepanelContext)
    if (!logic) {
        throw new Error('useSidepanel must be used within a SidepanelProvider')
    }
    return {
        isOpen: logic.state.isOpen,
        open: (content, options) => logic.open(content, options),
        close: () => logic.close(),
    }
}

function SidepanelPanel({
    state,
    logic,
}: {
    state: SidepanelLogicState
    logic: SidepanelLogic
}) {
    const [floatingStyles, setFloatingStyles] =
        React.useState<React.CSSProperties>({})
    const handleClose = () => logic.close()

    const startResize = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            const initialX = e.clientX
            const initialWidth = state.width || 400

            const onMouseMove = (moveE: MouseEvent) => {
                const deltaX = initialX - moveE.clientX
                const newWidth = Math.max(
                    200,
                    Math.min(window.innerWidth - 100, initialWidth + deltaX),
                )
                logic.setWidth(newWidth)
            }

            const onMouseUp = () => {
                document.removeEventListener('mousemove', onMouseMove)
                document.removeEventListener('mouseup', onMouseUp)
            }

            document.addEventListener('mousemove', onMouseMove)
            document.addEventListener('mouseup', onMouseUp)
        },
        [state.width, logic],
    )

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleClose()
            }
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [handleClose])

    useEffect(() => {
        if (!state.isOpen) return // Only set styles when open (content can load after)

        setFloatingStyles({
            position: 'fixed',
            top: 0,
            right: 0,
            width: `${state.width || 400}px`,
            height: '100vh',
        })
    }, [state.isOpen, state.width]) // Remove state.content dep

    if (!state.isOpen) return null

    return createPortal(
        <Overlay onClick={handleClose}>
            <Panel
                id="sidepanel-floating"
                $width={state.width || 400}
                style={floatingStyles}
                onClick={(e) => e.stopPropagation()}
            >
                <Resizer onMouseDown={startResize} />
                <CloseButtonContainer>
                    <Button
                        icon="close"
                        onClick={handleClose}
                        variant="ghost"
                        size="medium"
                    />
                </CloseButtonContainer>
                <Content>
                    {state.content || <PanelLoader>Loading...</PanelLoader>}
                </Content>
            </Panel>
        </Overlay>,
        document.body,
    )
}

interface SidepanelProviderProps {
    children: React.ReactNode
}

export function SidepanelProvider({ children }: SidepanelProviderProps) {
    const appContext = useAppContext()
    const { logic, state } = useLogic(SidepanelLogic, appContext)

    useEffect(() => {
        logic.initialize()
    }, [logic])

    const value = logic

    return (
        <SidepanelContext.Provider value={value}>
            {children}
            <SidepanelPanel state={state} logic={logic} />
        </SidepanelContext.Provider>
    )
}

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    justify-content: flex-end;
`

const Panel = styled.div<{ $width: number }>`
    height: 100vh;
    background: ${({ theme }) => theme.colors.background.bg1};
    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
    z-index: 10000;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-left: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
`

const Resizer = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    cursor: col-resize;
    background: transparent;
    z-index: 10001;
    &:hover {
        background: rgba(0, 0, 0, 0.1);
    }
`

const Content = styled.div`
    flex: 1;
    overflow-y: auto;
`

const CloseButtonContainer = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 10001;
`

const PanelLoader = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
`
